import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Calendar, 
  Users, 
  MapPin, 
  Award,
  TrendingUp,
  Star,
  Clock,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  ExternalLink,
  BarChart3,
  Target,
  Zap,
  Medal
} from 'lucide-react';
import { championshipsApi } from '../../services/api';
import { useSiteConfig } from '../../hooks/useSiteConfig';
import Breadcrumb from '../Common/Breadcrumb';

interface ChampionshipDetail {
  id: number;
  name: string;
  sport: string;
  level: string;
  description: string;
  founded_year: number;
  format: string;
  current_champion?: {
    team: string;
    year: number;
    score?: string;
    opponent?: string;
  };
  recent_champions: Array<{
    year: number;
    team: string;
    score?: string;
    opponent?: string;
    location?: string;
    mvp?: string;
  }>;
  statistics: {
    total_games: number;
    most_titles: { team: string; count: number };
    longest_streak: { team: string; years: string };
    highest_score?: string;
    total_teams?: number;
  };
  upcoming?: {
    date: string;
    location: string;
    teams_qualified?: number;
  };
}

const ChampionshipDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const siteConfig = useSiteConfig();
  const [expandedYear, setExpandedYear] = useState<number | null>(null);

  // Fetch championship details
  const { data: championshipData, isLoading: isLoadingChampionship } = useQuery({
    queryKey: ['championship', slug],
    queryFn: async () => {
      const response = await championshipsApi.getBySlug(slug!);
      return response.data;
    },
    enabled: !!slug,
  });

  // Fetch championship stats
  const { data: statsData, isLoading: isLoadingStats } = useQuery({
    queryKey: ['championship-stats', slug],
    queryFn: async () => {
      const response = await championshipsApi.getStats(slug!);
      return response.data;
    },
    enabled: !!slug,
  });

  // Fetch championship games (recent champions)
  const { data: gamesData, isLoading: isLoadingGames } = useQuery({
    queryKey: ['championship-games', slug],
    queryFn: async () => {
      const response = await championshipsApi.getGames(slug!);
      return response.data;
    },
    enabled: !!slug,
  });

  const isLoading = isLoadingChampionship || isLoadingStats || isLoadingGames;
  const error = null; // You can handle errors properly if needed

  // Transform API data to component format
  const championship: ChampionshipDetail | undefined = championshipData && statsData ? {
    id: parseInt(championshipData.id) || 0,
    name: championshipData.name,
    sport: championshipData.sport?.name || siteConfig.sport || '',
    level: championshipData.level?.name || siteConfig.level || '',
    description: championshipData.description || 'The premier championship event determining the ultimate champion.',
    founded_year: championshipData.first_year || 1967,
    format: 'Single elimination game', // This could come from API if available
    current_champion: gamesData && gamesData.results && gamesData.results.length > 0 ? {
      team: gamesData.results[0].winner?.name || gamesData.results[0].winner_name || '',
      year: gamesData.results[0].season?.start_year || new Date(gamesData.results[0].game_date).getFullYear(),
      score: `${gamesData.results[0].winner_score}-${gamesData.results[0].runner_up_score}`,
      opponent: gamesData.results[0].runner_up?.name || gamesData.results[0].runner_up_name || ''
    } : undefined,
    recent_champions: gamesData && gamesData.results ? gamesData.results.slice(0, 5).map((game: any) => ({
      year: game.season?.start_year || new Date(game.game_date).getFullYear(),
      team: game.winner?.name || game.winner_name || '',
      score: `${game.winner_score}-${game.runner_up_score}`,
      opponent: game.runner_up?.name || game.runner_up_name || '',
      location: game.venue?.city || '',
      mvp: game.mvp || ''
    })) : [],
    statistics: {
      total_games: statsData.total_games || 0,
      most_titles: statsData.team_championships && statsData.team_championships[0] ? {
        team: statsData.team_championships[0].team__name,
        count: statsData.team_championships[0].championships_won
      } : { team: 'N/A', count: 0 },
      longest_streak: statsData.longest_streak || { team: 'N/A', years: 'N/A' },
      highest_score: statsData.highest_score || undefined,
      total_teams: statsData.total_teams || undefined
    },
    upcoming: undefined // Will be populated when backend provides this data
  } : undefined;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-400">Loading championship details...</p>
        </div>
      </div>
    );
  }

  if (error || !championship) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-16 h-16 text-gray-600 mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-white mb-2">Championship Not Found</h3>
        <p className="text-gray-400 mb-6">We couldn't find details for this championship.</p>
        <Breadcrumb
          items={[{ label: 'All Championships', path: '/championships' }]}
          showBackButton={true}
          backButtonText="Back to Championships"
          backButtonPath="/championships"
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Breadcrumb Navigation */}
      <Breadcrumb
        items={[
          { label: 'All Championships', path: '/championships' },
          { label: championship?.name || 'Championship Details', isActive: true }
        ]}
      />

      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="gradient-text">{championship.name}</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl">
              {championship.description}
            </p>
          </div>
          <Trophy className="w-16 h-16 text-primary opacity-50" />
        </div>

        {/* Key Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="glass-card p-4"
          >
            <Calendar className="w-5 h-5 text-primary mb-2" />
            <div className="text-2xl font-bold text-white">{championship.founded_year}</div>
            <div className="text-sm text-gray-400">Founded</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="glass-card p-4"
          >
            <Trophy className="w-5 h-5 text-secondary mb-2" />
            <div className="text-2xl font-bold text-white">{championship.statistics.total_games}</div>
            <div className="text-sm text-gray-400">Total Games</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="glass-card p-4"
          >
            <Users className="w-5 h-5 text-accent mb-2" />
            <div className="text-2xl font-bold text-white">{championship.statistics.total_teams || 'N/A'}</div>
            <div className="text-sm text-gray-400">Teams Competing</div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4 }}
            className="glass-card p-4"
          >
            <Target className="w-5 h-5 text-success mb-2" />
            <div className="text-lg font-bold text-white">{championship.format}</div>
            <div className="text-sm text-gray-400">Format</div>
          </motion.div>
        </div>
      </motion.div>

      {/* Current Champion */}
      {championship.current_champion && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="glass-card p-8 mb-8 border-2 border-primary/30"
        >
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <Medal className="w-8 h-8 text-primary" />
                <h2 className="text-2xl font-bold text-white">Current Champion</h2>
              </div>
              <div className="text-3xl font-black text-primary mb-2">
                {championship.current_champion.team}
              </div>
              <div className="flex items-center space-x-6 text-gray-400">
                <span className="flex items-center space-x-2">
                  <Calendar className="w-4 h-4" />
                  <span>{championship.current_champion.year}</span>
                </span>
                {championship.current_champion.score && (
                  <span className="flex items-center space-x-2">
                    <BarChart3 className="w-4 h-4" />
                    <span>Score: {championship.current_champion.score}</span>
                  </span>
                )}
                {championship.current_champion.opponent && (
                  <span className="flex items-center space-x-2">
                    <Users className="w-4 h-4" />
                    <span>vs {championship.current_champion.opponent}</span>
                  </span>
                )}
              </div>
            </div>
            <Trophy className="w-24 h-24 text-primary/20" />
          </div>
        </motion.div>
      )}

      {/* Statistics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
      >
        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Star className="w-6 h-6 text-primary" />
            <h3 className="text-lg font-bold text-white">Most Titles</h3>
          </div>
          <div className="text-2xl font-bold text-primary">{championship.statistics.most_titles.team}</div>
          <div className="text-gray-400">{championship.statistics.most_titles.count} Championships</div>
        </div>

        <div className="glass-card p-6">
          <div className="flex items-center space-x-3 mb-4">
            <TrendingUp className="w-6 h-6 text-secondary" />
            <h3 className="text-lg font-bold text-white">Longest Streak</h3>
          </div>
          <div className="text-2xl font-bold text-secondary">{championship.statistics.longest_streak.team}</div>
          <div className="text-gray-400">{championship.statistics.longest_streak.years}</div>
        </div>

        {championship.statistics.highest_score && (
          <div className="glass-card p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Zap className="w-6 h-6 text-accent" />
              <h3 className="text-lg font-bold text-white">Highest Score</h3>
            </div>
            <div className="text-2xl font-bold text-accent">{championship.statistics.highest_score}</div>
            <div className="text-gray-400">Record Game</div>
          </div>
        )}
      </motion.div>

      {/* Recent Champions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="mb-8"
      >
        <h2 className="text-2xl font-bold text-white mb-6">Recent Champions</h2>
        <div className="space-y-4">
          {championship.recent_champions.map((champion, index) => (
            <motion.div
              key={champion.year}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 + index * 0.1 }}
              className="glass-card p-4"
            >
              <div 
                className="flex items-center justify-between cursor-pointer"
                onClick={() => setExpandedYear(expandedYear === champion.year ? null : champion.year)}
              >
                <div className="flex items-center space-x-4">
                  <div className="text-2xl font-bold text-primary">{champion.year}</div>
                  <div>
                    <div className="text-lg font-semibold text-white">{champion.team}</div>
                    {champion.opponent && (
                      <div className="text-sm text-gray-400">
                        defeated {champion.opponent} {champion.score}
                      </div>
                    )}
                  </div>
                </div>
                {expandedYear === champion.year ? (
                  <ChevronUp className="w-5 h-5 text-gray-400" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-400" />
                )}
              </div>
              
              {expandedYear === champion.year && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="mt-4 pt-4 border-t border-gray-700"
                >
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    {champion.location && (
                      <div className="flex items-center space-x-2 text-gray-400">
                        <MapPin className="w-4 h-4" />
                        <span>{champion.location}</span>
                      </div>
                    )}
                    {champion.mvp && (
                      <div className="flex items-center space-x-2 text-gray-400">
                        <Award className="w-4 h-4" />
                        <span>MVP: {champion.mvp}</span>
                      </div>
                    )}
                    {champion.score && (
                      <div className="flex items-center space-x-2 text-gray-400">
                        <BarChart3 className="w-4 h-4" />
                        <span>Final Score: {champion.score}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Upcoming Event */}
      {championship.upcoming && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
          className="glass-card p-8 border-2 border-secondary/30"
        >
          <div className="flex items-center space-x-3 mb-4">
            <Clock className="w-6 h-6 text-secondary" />
            <h2 className="text-2xl font-bold text-white">Next Championship</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="text-sm text-gray-400 mb-1">Date</div>
              <div className="text-lg font-semibold text-white">{championship.upcoming.date}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-1">Location</div>
              <div className="text-lg font-semibold text-white">{championship.upcoming.location}</div>
            </div>
            {championship.upcoming.teams_qualified && (
              <div>
                <div className="text-sm text-gray-400 mb-1">Teams Qualified</div>
                <div className="text-lg font-semibold text-white">{championship.upcoming.teams_qualified}</div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ChampionshipDetail;